/**
 * The Project overview's Week, Month and Project reviews (MB's R101, 26 Oct 2026;
 * docs/specs/project-reporting/overview-estimates.md): the Day review's figures summed over a period. Every figure is
 * the Day review's own (`entryRows`, the rows `daySummary` gives day by day), so a week's total is the sum of its
 * days and the Project review's total is the sum of every day:
 *
 * - flights: flight entries (TJR counts a ferry-only day as a flight, as the Day review and the Job Summary);
 * - survey, comp box, cals, ferry and flown hours (`hoursFlown`, decision 162); fuel used;
 * - km flown (decision 32 basis) and accepted (`acceptedM`, ADR-013) of each flight's lines; km rejected: the flown
 *   km of rejected lines and the unaccepted part of part-accepted ones (`reflyM`, the Pilots box's refly km);
 * - lines: line statuses flown; standby, maintenance and suspended days: Σ `days_alloc` by entry type.
 *
 * Weeks are ISO weeks, Monday to Sunday, numbered as the weekly report (WK42 is 12 to 18 Oct 2026).
 */
import { isoWeekOf, isoWeekMonday } from './dates.js';
import { entryRows, reflyM } from './day-summary.js';
import type { DailyEntry, Flight, IsoDate, LineStatus } from './model.js';
import { entryLineStatuses } from './tjr-adapter.js';

export type ReviewKind = 'week' | 'month' | 'project';

export interface ReviewPeriod {
  readonly kind: ReviewKind;
  /** `2026-W42`, `2026-10` or `project`. */
  readonly key: string;
  /** "WK42 · 12 Oct to 18 Oct 2026", "October 2026", "Whole project". */
  readonly label: string;
  readonly from: IsoDate;
  readonly to: IsoDate;
}

export interface PeriodTotals {
  readonly entries: number;
  readonly flights: number;
  /** Dates with flight hours. */
  readonly days_flown: number;
  readonly survey_h: number;
  readonly comp_box_h: number;
  readonly cal_h: number;
  readonly ferry_h: number;
  readonly hours_flown_h: number;
  readonly fuel_used_l: number;
  readonly flown_km: number;
  readonly accepted_km: number;
  readonly rejected_km: number;
  readonly lines: number;
  readonly standby_days: number;
  readonly maintenance_days: number;
  readonly suspended_days: number;
}

export interface PeriodRow {
  /** The date (Week and Month reviews) or the ISO week key (Project review). */
  readonly key: string;
  readonly label: string;
  readonly totals: PeriodTotals;
}

export interface PeriodReview {
  readonly period: ReviewPeriod;
  /** One row per day with entries (Week, Month) or per ISO week with entries (Project), oldest first. */
  readonly rows: readonly PeriodRow[];
  readonly totals: PeriodTotals;
}

export const REVIEW_KIND_LABEL: Readonly<Record<ReviewKind | 'day', string>> = {
  day: 'Day review',
  week: 'Week review',
  month: 'Month review',
  project: 'Project review',
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const utc = (d: IsoDate): Date => new Date(`${d}T00:00:00Z`);
const iso = (d: Date): IsoDate => d.toISOString().slice(0, 10);
const shortDay = (d: IsoDate): string =>
  `${Number(d.slice(8, 10))} ${MONTHS[Number(d.slice(5, 7)) - 1]!.slice(0, 3)}`;

function weekOf(d: IsoDate): ReviewPeriod {
  const w = isoWeekOf(utc(d));
  const monday = isoWeekMonday(w.isoYear, w.week);
  const from = iso(monday);
  const to = iso(new Date(monday.getTime() + 6 * 86_400_000));
  const nn = String(w.week).padStart(2, '0');
  return {
    kind: 'week',
    key: `${w.isoYear}-W${nn}`,
    label: `WK${nn} · ${shortDay(from)} to ${shortDay(to)} ${to.slice(0, 4)}`,
    from,
    to,
  };
}

function monthOf(d: IsoDate): ReviewPeriod {
  const y = Number(d.slice(0, 4));
  const m = Number(d.slice(5, 7));
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, '0');
  return {
    kind: 'month',
    key: `${y}-${mm}`,
    label: `${MONTHS[m - 1]} ${y}`,
    from: `${y}-${mm}-01`,
    to: `${y}-${mm}-${String(last).padStart(2, '0')}`,
  };
}

const live = (entries: readonly DailyEntry[]): DailyEntry[] => entries.filter((e) => e.deleted_at === null);

/** The periods of a kind that hold daily entries, newest first; the Project review is one period, the whole job. */
export function reviewPeriods(kind: ReviewKind, entries: readonly DailyEntry[]): ReviewPeriod[] {
  const dates = [...new Set(live(entries).map((e) => e.date))].sort();
  if (dates.length === 0) return [];
  if (kind === 'project')
    return [{ kind, key: 'project', label: 'Whole project', from: dates[0]!, to: dates[dates.length - 1]! }];
  const of = kind === 'week' ? weekOf : monthOf;
  const byKey = new Map<string, ReviewPeriod>();
  for (const d of dates) {
    const p = of(d);
    if (!byKey.has(p.key)) byKey.set(p.key, p);
  }
  return [...byKey.values()].sort((a, b) => b.from.localeCompare(a.from));
}

const ZERO: PeriodTotals = {
  entries: 0,
  flights: 0,
  days_flown: 0,
  survey_h: 0,
  comp_box_h: 0,
  cal_h: 0,
  ferry_h: 0,
  hours_flown_h: 0,
  fuel_used_l: 0,
  flown_km: 0,
  accepted_km: 0,
  rejected_km: 0,
  lines: 0,
  standby_days: 0,
  maintenance_days: 0,
  suspended_days: 0,
};

interface EntryFigures {
  readonly entry: DailyEntry;
  readonly hours_flown_h: number;
  readonly flown_km: number;
  readonly accepted_km: number;
  readonly rejected_km: number;
  readonly lines: number;
}

function totalsOf(rows: readonly EntryFigures[]): PeriodTotals {
  const sum = (f: (r: EntryFigures) => number): number => rows.reduce((a, r) => a + f(r), 0);
  const days = (type: DailyEntry['entry_type']): number =>
    sum((r) => (r.entry.entry_type === type ? r.entry.days_alloc : 0));
  return {
    entries: rows.length,
    flights: rows.filter((r) => r.entry.entry_type === 'flight').length,
    days_flown: new Set(rows.filter((r) => r.hours_flown_h > 0).map((r) => r.entry.date)).size,
    survey_h: sum((r) => r.entry.svy_hours),
    comp_box_h: sum((r) => r.entry.cbx_hours),
    cal_h: sum((r) => r.entry.cal_hours),
    ferry_h: sum((r) => r.entry.fry_hours),
    hours_flown_h: sum((r) => r.hours_flown_h),
    fuel_used_l: sum((r) => r.entry.fuel_used_l),
    flown_km: sum((r) => r.flown_km),
    accepted_km: sum((r) => r.accepted_km),
    rejected_km: sum((r) => r.rejected_km),
    lines: sum((r) => r.lines),
    standby_days: days('standby'),
    maintenance_days: days('maintenance'),
    suspended_days: days('suspended'),
  };
}

/** Each live entry's figures, worked out once however many periods read them. */
function entryFigures(
  entries: readonly DailyEntry[],
  statuses: readonly LineStatus[],
  flights: readonly Flight[],
): EntryFigures[] {
  const all = live(entries);
  const ofEntry = entryLineStatuses(all, statuses, flights);
  return entryRows(all, [], statuses, flights).map((r) => ({
    entry: r.entry,
    hours_flown_h: r.hours_flown_h,
    flown_km: r.flown_km,
    accepted_km: r.accepted_km,
    rejected_km: reflyM(ofEntry.get(r.entry.id) ?? []) / 1000,
    lines: r.lines,
  }));
}

/** The review of one period: a row per day (Week, Month) or per ISO week (Project), and the period's totals. */
export function periodReview(
  period: ReviewPeriod,
  entries: readonly DailyEntry[],
  statuses: readonly LineStatus[],
  flights: readonly Flight[],
): PeriodReview {
  return reviewOf(period, entryFigures(entries, statuses, flights));
}

/** Every period of a kind, newest first (`reviewPeriods`), each as `periodReview` gives it (the Overview, R246). */
export function periodReviews(
  kind: ReviewKind,
  entries: readonly DailyEntry[],
  statuses: readonly LineStatus[],
  flights: readonly Flight[],
): PeriodReview[] {
  const all = entryFigures(entries, statuses, flights);
  return reviewPeriods(kind, entries).map((p) => reviewOf(p, all));
}

function reviewOf(period: ReviewPeriod, all: readonly EntryFigures[]): PeriodReview {
  const figures = all.filter((r) => r.entry.date >= period.from && r.entry.date <= period.to);
  const groups = new Map<string, { label: string; rows: EntryFigures[] }>();
  for (const f of figures) {
    const w = period.kind === 'project' ? weekOf(f.entry.date) : null;
    const key = w ? w.key : f.entry.date;
    const g = groups.get(key) ?? { label: w ? w.label : f.entry.date, rows: [] };
    g.rows.push(f);
    groups.set(key, g);
  }
  const rows = [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, g]) => ({ key, label: g.label, totals: totalsOf(g.rows) }));
  return { period, rows, totals: figures.length > 0 ? totalsOf(figures) : ZERO };
}
